import { handleActions } from "redux-actions";
import { DisplayNamesActionTypes, FilteredDisplayNamesActionTypes, UpdateDisplayNamesItemsActionTypes } from "../actions/displayNames";
import { AsyncStates, sortingConditonKeys } from "../../constants";

export interface DisplayNamesState {
  status: AsyncStates,
  statusWithoutSpinner: AsyncStates,
  error: string,
  data: {
    [key: string]: any
  },
  inventoryData: any
};

export interface FilteredDisplayNamesState {
  filterStatus: AsyncStates,
  error: string,
  data: {
    [key: string]: any
  }
};

const defaultState: DisplayNamesState = {
  status: AsyncStates.INITIAL,
  statusWithoutSpinner: AsyncStates.INITIAL,
  error: '',
  inventoryData: {},
  data: {
    ingredients: {},
    processing: {},
    characterizations: {},
    properties: {},
    ingredients_category: [],
    properties_category: [],
    material: {},
    application: [],
    grade: [],
    order_type: [],
    stages: {},
    project_tags: [],
    language: "en",
    descriptor: { ingredients: "Ingredients", characterizations: "Characterizations", processing: "Processing", properties: "Properties" },
  }
}

const defaultFilteredState: FilteredDisplayNamesState = {
  filterStatus: AsyncStates.INITIAL,
  error: '',
  data: {},
}

const sortObjectKeys = (input: any) => {
  if (Array.isArray(input)) {
    return input.sort((a: string, b: string) => a?.localeCompare(b));
  }
  const sortedArray = Object.entries(input || {}).sort((a, b) => a[0].localeCompare(b[0]));
  return Object.fromEntries(sortedArray);
};


const displayNamesReducer = handleActions<DisplayNamesState, any>(
  {
    [DisplayNamesActionTypes.REQUEST]: (state, action: any) => ({
      ...state,
      ...(!action.payload?.backgroundFetch && { status: AsyncStates.LOADING }),
      statusWithoutSpinner: AsyncStates.LOADING,
      error: "",
    }),
    [DisplayNamesActionTypes.SUCCESS]: (state, action) => {
      const newDataObj: any = JSON.parse(JSON.stringify(state.data))
      const displayNames = action.payload
      const constantObjectIterator = ["ingredients", "processing", "characterizations", "properties", "material"]
      const constantArrayIterator = ["application", "grade", "order_type", "ingredients_category", "properties_category"]
      for (let i of constantObjectIterator) {
        if (Object.keys(displayNames || {}).includes(i)) {
          displayNames[i].forEach((record: any) => {
            if (i === "ingredients") {
              newDataObj[i][record.identifier] = {
                ...record,
                category_id: record?.category,
                category: displayNames?.ingredients_category?.find((res: any) => res?.category_id === record?.category)?.name
              }
            } else if (i === "properties") {
              newDataObj[i][record.identifier] = {
                ...record,
                category_id: record?.category,
                category: displayNames?.properties_category?.find((res: any) => res?.category_id === record?.category)?.name
              }
            } else {
              newDataObj[i][record.identifier] = { ...record }
            }
          })
        }
      }
      for (let i of constantArrayIterator) {
        if (Object.keys(displayNames || {}).includes(i)) {
          newDataObj[i] = displayNames[i]
        }
      }
      if (Object.keys(displayNames || {}).includes("stages")) {
        newDataObj.stages = displayNames.stages.reduce((obj: any, element: any) => ({ ...obj, [element.identifier]: element.name_translated?.[displayNames?.language] || element?.name }), {})
      }
      const resultObj = { ...displayNames, ...newDataObj }
      const arrangeByKeys = [...sortingConditonKeys, ...Object.keys(resultObj || {})]
      const rearrangedDisplayName: any = {};
      arrangeByKeys.forEach((key: string) => {
        if (resultObj.hasOwnProperty(key)) {
          if (!["stages", "ingredients_category", 'properties_category'].includes(key)) {
            rearrangedDisplayName[key] = sortObjectKeys(resultObj[key])
          } else {
            rearrangedDisplayName[key] = resultObj[key]
          }
        }
      });

      const inventoryData = {
        ...(action.payload ?? {}),
        "properties": !action.payload?.["properties"]?.length ? [] : action.payload?.["properties"].map((property: any) => ({
          ...property,
          category: action.payload?.properties_category?.find((res: any) => res?.category_id === property?.category)?.name
        }))
      }

      return ({
        ...state,
        status: AsyncStates.SUCCESS,
        statusWithoutSpinner: AsyncStates.SUCCESS,
        data: rearrangedDisplayName,
        inventoryData: inventoryData,
      })
    },
    [DisplayNamesActionTypes.FAILURE]: (state) => ({
      ...state,
      status: AsyncStates.ERROR,
      statusWithoutSpinner: AsyncStates.ERROR,
    }),
    [DisplayNamesActionTypes.UPDATE]: (state, action) => {
      const data: { name: string, unit: string, category: string, property_type: string, identifier: string }[] = action.payload.displayNames.data
      const newDataState = JSON.parse(JSON.stringify(state.data))
      data.forEach((res: any) => {
        const { property_type, name, identifier, unit, category } = res
        newDataState[property_type][identifier] = { category, unit, name }
      })
      return ({
        ...state,
        data: newDataState
      })
    },
    [DisplayNamesActionTypes.CLEANUP]: (state) => ({
      ...state,
      status: AsyncStates.INITIAL,
      statusWithoutSpinner: AsyncStates.INITIAL,
      error: "",
      data: {}
    }),
    [UpdateDisplayNamesItemsActionTypes.REQUEST]: (state, action) => {
      const newDataObj: any = JSON.parse(JSON.stringify(state.data))
      const inventoryData: any = JSON.parse(JSON.stringify(state.inventoryData))
      const apiPayload = action.payload


      if (apiPayload?.type && apiPayload?.data && typeof apiPayload?.data === "object") {
        const type = apiPayload?.type
        const dataToAdd = apiPayload?.data
        const identifiers = Object.keys(apiPayload?.data ?? {})

        if (newDataObj?.[type]) {
          newDataObj[type] = {
            ...(newDataObj?.[type] ?? {}),
            ...identifiers.reduce((acc, curr) => {
              if (newDataObj?.[type]?.[curr]) {
                acc = {
                  ...acc,
                  [curr]: {
                    ...(newDataObj?.[type]?.[curr] ?? {}),
                    ...(dataToAdd?.[curr] ?? {})
                  }
                }
              }
              return acc
            }, {})
          }
        }

        if (inventoryData?.[type] && type === "ingredients") {
          inventoryData[type] = inventoryData?.[type]?.map((item: any) => {
            if (identifiers.includes(item.inventory_id)) {
              return {
                ...item,
                ...(dataToAdd?.[item.inventory_id] ?? {})
              }
            }
            return item
          }, [])
        }
      }

      return {
        ...state,
        inventoryData: inventoryData,
        data: newDataObj
      }
    },

  },
  defaultState
);


export const FilteredDisplayNamesReducer = handleActions<FilteredDisplayNamesState>(
  {
    [FilteredDisplayNamesActionTypes.REQUEST]: (state) => ({
      ...state,
      filterStatus: AsyncStates.LOADING,
      error: "",
    }),
    [FilteredDisplayNamesActionTypes.SUCCESS]: (state, action) => ({
      ...state,
      filterStatus: AsyncStates.SUCCESS,
      data: action.payload.data
    }),
    [FilteredDisplayNamesActionTypes.FAILURE]: (state, action) => ({
      ...state,
      filterStatus: AsyncStates.ERROR,
      error: action.payload.error,
    }),
    [FilteredDisplayNamesActionTypes.CLEANUP]: (state) => ({
      ...state,
      filterStatus: AsyncStates.INITIAL,
      error: "",
      data: {}
    }),
  },
  defaultFilteredState
);

export default displayNamesReducer;
